import { useMemo } from "react";
import { Permission, SubPermissionsObj } from "src/api/types";
import { getAuthUserInfo } from "../api/token-util";

export type PermissionAction = keyof SubPermissionsObj;

export const extractSubPermission = (perm: any, key: PermissionAction): boolean => {
  if (!perm) return false;
  const subsObj = perm.subPermissions || perm.sub_permissions || perm;
  if (subsObj && (subsObj[key] === true || subsObj[key] === 1 || subsObj[key] === "true")) {
    return true;
  }
  const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  if (subsObj && (subsObj[camelKey] === true || subsObj[camelKey] === 1 || subsObj[camelKey] === "true")) {
    return true;
  }
  if (Array.isArray(perm.actions)) {
    const rawKey = key.replace(/^can_/, "").toLowerCase();
    return perm.actions.map((a: any) => String(a).toLowerCase().trim()).includes(rawKey);
  }
  return false;
};

/**
 * Hook to check module and granular action permissions for the current authenticated user.
 * 
 * Usage:
 * 1. For a specific page/module:
 *    const { canView, canExport, canAdd, canEdit, canDelete } = usePermissions(permissions.DASHBOARD);
 *    // or with fallbacks:
 *    const { canView, canExport } = usePermissions([permissions.INDIVIDUAL_USERS, permissions.USER_MANAGEMENT]);
 * 
 * 2. As a general permission checker:
 *    const { hasPermission, getModulePermission } = usePermissions();
 *    if (hasPermission("user management", "can_export")) { ... }
 */
export function usePermissions(moduleName?: string | string[]) {
  const userInfo = getAuthUserInfo();
  const permissions: Permission[] = useMemo(() => {
    return (userInfo?.permissions || userInfo?.role?.permissions || []) as Permission[];
  }, [userInfo]);

  const getModulePermission = (name: string | string[]): Permission | undefined => {
    if (!name || !Array.isArray(permissions)) return undefined;
    const names = Array.isArray(name) ? name : [name];
    for (const n of names) {
      if (!n) continue;
      const target = n.toLowerCase().trim();
      const found = permissions.find((p: any) => {
        const pName = (p?.name || p?.permission_name || p?.permission_key)?.toLowerCase()?.trim();
        return pName === target;
      });
      if (found) return found;
    }
    return undefined;
  };

  const hasPermission = (name: string | string[], action?: PermissionAction): boolean => {
    const perm = getModulePermission(name);
    if (!perm) return false;
    if (!action) return true;
    return extractSubPermission(perm, action);
  };

  const currentModulePermission = useMemo(() => {
    return moduleName ? getModulePermission(moduleName) : undefined;
  }, [moduleName, permissions]);

  const canAdd = extractSubPermission(currentModulePermission, "can_add");
  const canEdit = extractSubPermission(currentModulePermission, "can_edit");
  const canList = extractSubPermission(currentModulePermission, "can_list");
  const canView = extractSubPermission(currentModulePermission, "can_view");
  const canDelete = extractSubPermission(currentModulePermission, "can_delete");
  const canApproveReject = extractSubPermission(currentModulePermission, "can_approve_reject");
  const canExport = extractSubPermission(currentModulePermission, "can_export");
  const canDownload = extractSubPermission(currentModulePermission, "can_download");
  const canBlock = extractSubPermission(currentModulePermission, "can_block");

  // If module exists, user has access if can_view or can_list is true (or if any action flag is granted)
  const hasAccess = Boolean(
    currentModulePermission &&
    (canView || canList || canAdd || canEdit || canExport || canDownload || canDelete || canApproveReject || canBlock)
  );

  return {
    permissions,
    currentModulePermission,
    hasAccess,
    canAdd,
    canEdit,
    canList,
    canView,
    canDelete,
    canApproveReject,
    canExport,
    canDownload,
    canBlock,
    hasPermission,
    getModulePermission,
  };
}

export default usePermissions;
